import { useContext, useEffect } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'

import { browserHistory } from '../../services/history'
import { AnalyticsService, categories, actions as analyticsActions } from '../../services/analytics'

import { context } from '../context'
import { actions$ } from '../middleware'
import * as TYPES from '../types'
import * as actions from '../actions'
import { clearBookingState } from '../booking-persistence'
import { BOOKING_ROUTES, COLD_ENTRY_STEPS, STEPS } from '../../pages/booking/components/steps'
import { firstBookingStepPath } from '../../lib/booking-flow'

import { useOrdersApi } from './use-orders-api'
import { usePromoCodesApi } from './use-promo-codes-api'
import { useSearchParams } from './use-search-params'
import { useServicesApi } from './use-services-api'
import { useUsersApi } from './use-users-api'
import { useConfigApi } from './use-config-api'

const takeAllStartup = {
  userStatus: false,
  services: false,
  done: false
}

// The last date and time slot "Select date" / "Select time" were sent for:
// true only for a value that is set and differs from the last one.
const legacyLast = {}
const legacyChanged = (key, value) => {
  const v = value instanceof Date ? (isNaN(value) ? '' : value.toISOString().slice(0, 10)) : String(value || '')
  if (!v || legacyLast[key] === v) return false
  legacyLast[key] = v
  return true
}

export const useSideEffects = () => {
  const { dispatch } = useContext(context)
  const bookingForm = useFormContext()
  const { apiGetConfig } = useConfigApi()
  const { apiOrderFetchAll } = useOrdersApi()
  const { applyPromoCodeParam, applyServiceParam } = useSearchParams()
  const { applyPromoCodeOnSignIn } = usePromoCodesApi()
  const { apiServicesFetchAll } = useServicesApi()
  const { apiUserStatus } = useUsersApi()
  const step = useRouteMatch('/booking/:step')?.params?.step
  const isCheckoutSuccess = !!useRouteMatch('/checkout/success/:sessionId')

  useEffect(() => {
    const sideEffects = actions$.subscribe(action => {
      const { type } = action

      switch (type) {
        case TYPES.STARTUP_START:
          // If returning from Stripe after successful payment, clear saved booking state immediately
          if (isCheckoutSuccess) clearBookingState()
          // A cold load of any other booking step (/booking/finish, a retired
          // four-step URL) restarts the booking on Book & pay, which puts any
          // draft back. browserHistory keeps the query, so ?service= /
          // ?extra= survive the restart.
          if (step && !isCheckoutSuccess && !COLD_ENTRY_STEPS.includes(step)) {
            browserHistory.replace(firstBookingStepPath())
          }
          apiGetConfig()
          apiUserStatus()
          apiServicesFetchAll()
          break
        case TYPES.SET_USER:
          // Handle null user gracefully - default values only apply when undefined, not null
          const { user } = action
          if (user) {
            // GA4 user_id + PostHog identify on users.id (no PII) — ANALYTICS.md §9
            AnalyticsService.identify(user)
          }
          break
        case TYPES.SIGN_OUT:
          AnalyticsService.reset()
          window.localStorage.removeItem('token')
          window.localStorage.removeItem('isGuest')
          break
        case TYPES.API_USERS_SIGN_IN_SUCCESS:
        case TYPES.API_USERS_SIGN_UP_SUCCESS:
          apiOrderFetchAll()
          applyPromoCodeOnSignIn()
          if (/^\/user/.test(browserHistory.location.pathname)) browserHistory.push('/')
          break
        case TYPES.API_ORDERS_CREATE_SUCCESS:
          clearBookingState()
          dispatch(actions.resetBookingForm())
          bookingForm.reset()
          // If already on /checkout/success page, stay there instead of redirecting
          if (!/^\/checkout\/success/.test(browserHistory.location.pathname)) {
            browserHistory.push(BOOKING_ROUTES[STEPS.FINISH])
          }
          break
        case TYPES.API_USERS_STATUS_SUCCESS:
          apiOrderFetchAll()
          takeAllStartup.userStatus = true
          break
        case TYPES.API_USERS_STATUS_FAILURE:
          takeAllStartup.userStatus = true
          break
        case TYPES.API_SERVICES_FETCH_ALL_SUCCESS:
          takeAllStartup.services = true

          // Book & pay puts its own draft back (StepBook, useBookDraft),
          // whichever way the visitor gets there, Stripe's cancel link to
          // /booking/book included. The old four-step screens' restore that
          // sat here went with them on 12 Oct 2026.
          break
        case TYPES.API_SERVICES_FETCH_ALL_FAILURE:
          takeAllStartup.services = true
          break
        case TYPES.STARTUP_FINISH:
          applyPromoCodeParam()
          applyServiceParam()
          break
        // "Select date" / "Select time" only when the value really changes
        // (19 Oct 2026): Book & pay mirrors the whole form into the store on
        // every change (StepBook dispatchDetails), so these fired again on
        // every keystroke anywhere, about 7 pairs during one plate. "Select
        // location" is gone: it fired per keystroke in the address, and the
        // Car location events say more (ANALYTICS.md).
        case TYPES.SET_DATE:
          if (legacyChanged('date', action.date)) AnalyticsService.trackEvent(categories.booking, analyticsActions.setlectDate)
          break
        case TYPES.SET_TIME_SLOT:
          if (legacyChanged('timeSlot', action.timeSlot)) AnalyticsService.trackEvent(categories.booking, analyticsActions.setlectTime)
          break
        case TYPES.API_PROMO_CODES_APPLY_SUCCESS:
          if (/^\/user\/promo_code/.test(browserHistory.location.pathname)) browserHistory.push('/booking')
          break
        case TYPES.API_USERS_SIGN_IN_FAILURE:
          dispatch(actions.setMessage(action.error || 'Login failed. Please check email & password', null, null, '#f44336'))
          break
        case TYPES.API_USERS_SIGN_UP_FAILURE:
          dispatch(actions.setMessage(action.error || 'Sign up failed. Please try again', null, null, '#f44336'))
          break
      }

      if (!takeAllStartup.done) {
        const allDone = Object.values(takeAllStartup).filter(it => !it)

        if (allDone.length === 1) {
          takeAllStartup.done = true
          dispatch(actions.startupFinish())
        }
      }
    })

    return () => sideEffects.unsubscribe()
  }, [apiGetConfig, apiOrderFetchAll, apiServicesFetchAll, apiUserStatus, applyPromoCodeOnSignIn, applyPromoCodeParam, applyServiceParam, bookingForm, dispatch, isCheckoutSuccess, step])

  // useEffect(() => {
  //   FB.getLoginStatus(function(response) {
  //     console.log('FB.getLoginStatus response:', response)
  //     if (response.status === 'unknown') {
  //       FB.login(
  //         function(response) {
  //           console.log(response)
  //         },
  //         { scope: 'email' }
  //       )
  //     } else if (response.status === 'not_authorized') {
  //     } else if (response.status === 'connected') {
  //       console.log('Welcome!  Fetching your information.... ')
  //       FB.api('/me', { fields: 'first_name, last_name, email' }, function(response) {
  //         console.log('Successful login for ', response)
  //       })
  //     }
  //   })
  // }, [])
}
